// Badging form filler UI (2026-06-11).
// Two modals used from CustomerDetail's Badging Documents card:
//   <MappingModal>  — per-PDF field mapping: AI proposes, human confirms.
//   <FillWizard>    — tick forms → pick roster employees → answer the gaps
//                     once → generate filled PDFs → optional email.
// Desktop-oriented (the Reqs page itself is desktop-first).

import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles } from './UI';
import DropZone from './DropZone';

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px', zIndex: 1000, overflowY: 'auto',
};
const card = (w) => ({
  background: '#fff', borderRadius: 8, padding: 24,
  maxWidth: w, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
});
const h2 = { margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1F2937' };
const sub = { fontSize: 11, color: '#6B7280', marginBottom: 14 };
const thStyle = { textAlign: 'left', fontSize: 10, textTransform: 'uppercase', color: '#6B7280', padding: '4px 6px', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' };
const tdStyle = { padding: '4px 6px', borderBottom: '1px solid #F3F4F6', fontSize: 12, verticalAlign: 'top' };
const smallInput = { ...styles.input, fontSize: 11, padding: '4px 6px' };

const SOURCE_OPTIONS = [
  { value: 'employee', label: 'Roster (auto)' },
  { value: 'employee_ask', label: 'Ask per employee' },
  { value: 'shared_ask', label: 'Ask once per run' },
  { value: 'constant', label: 'Constant value' },
  { value: 'skip', label: 'Skip (leave blank)' },
];

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

// Canonical document types — the SAME list drives the customer's
// required-documents picker and the employee document upload labels, so
// the match between them never depends on consistent typing.
export const DOC_TYPES = [
  'Drug Test',
  'Safety Council Training',
  'TWIC',
  "Driver's License",
  'Background Check',
  'Medical / Fit Test',
  'Training Certificate',
];

// ---------------------------------------------------------------------------
// Mapping editor

export function MappingModal({ file, onClose, onSaved, onError }) {
  const [pdfFields, setPdfFields] = useState(null);   // [{name,type}]
  const [profileKeys, setProfileKeys] = useState({});
  const [rows, setRows] = useState(1);
  const [fields, setFields] = useState([]);           // mapping entries, one per pdf field
  const [busy, setBusy] = useState('');               // '', 'extract', 'save'

  // Merge the live PDF field list with whatever mapping exists: every PDF
  // field gets exactly one row; signature/other types are forced to skip.
  const applyMapping = (live, mapping) => {
    const byName = new Map((mapping?.fields || []).map(f => [f.pdf_field, f]));
    setRows(mapping?.employee_rows || 1);
    setFields(live.map(pf => {
      const m = byName.get(pf.name);
      const fillable = pf.type === 'text' || pf.type === 'checkbox';
      return m ? { ...m, field_type: pf.type === 'checkbox' ? 'checkbox' : 'text', _pdf_type: pf.type }
        : {
            pdf_field: pf.name,
            field_type: pf.type === 'checkbox' ? 'checkbox' : 'text',
            _pdf_type: pf.type,
            row: null,
            source: fillable ? 'skip' : 'skip',
            source_key: null, ask_key: null, question: null, constant_value: null,
          };
    }));
  };

  useEffect(() => {
    Promise.all([api.badgingFields(file.id), api.getBadgingMapping(file.id)])
      .then(([ff, mapping]) => {
        setPdfFields(ff.fields);
        setProfileKeys(ff.profile_keys || {});
        applyMapping(ff.fields, mapping);
      })
      .catch(e => onError(e.message));
  }, [file.id]);

  const extract = async () => {
    setBusy('extract');
    try {
      const mapping = await api.extractBadgingMapping(file.id);
      applyMapping(pdfFields, mapping);
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  const save = async (confirm) => {
    setBusy('save');
    try {
      const clean = fields.map(({ _pdf_type, ...f }) => ({
        ...f,
        row: f.row ? parseInt(f.row, 10) : null,
        ask_key: (f.source === 'employee_ask' || f.source === 'shared_ask')
          ? (f.ask_key || slug(f.question || f.pdf_field)) : null,
      }));
      await api.saveBadgingMapping(file.id, { employee_rows: rows, fields: clean, confirm });
      onSaved();
      onClose();
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  const set = (i, k, v) => setFields(prev => prev.map((f, idx) => idx === i ? { ...f, [k]: v } : f));

  // Copy Row 1's choices to every matching RowN field (same field name with
  // the row suffix swapped) — fixes AI inconsistency across rows in one
  // click. Row numbers are preserved; only the source choices copy.
  const copyRow1Pattern = () => {
    setFields(prev => {
      const baseOf = (name) => name.replace(/row\s*_?\d+\s*$/i, '').trim();
      const hasRowSuffix = (name) => /row\s*_?\d+\s*$/i.test(name);
      const templates = new Map();
      for (const f of prev) {
        if (f.row === 1 && hasRowSuffix(f.pdf_field)) templates.set(baseOf(f.pdf_field), f);
      }
      return prev.map(f => {
        if (!hasRowSuffix(f.pdf_field) || f.row === 1 || f._pdf_type === 'signature') return f;
        const t = templates.get(baseOf(f.pdf_field));
        if (!t) return f;
        return {
          ...f,
          source: t.source, source_key: t.source_key, ask_key: t.ask_key,
          question: t.question, constant_value: t.constant_value,
        };
      });
    });
  };

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card(1100)}>
        <h2 style={h2}>🪄 Field Mapping — {file.filename}</h2>
        <div style={sub}>
          Tell the filler where each PDF field gets its value. ✨ Auto-map reads the form with AI and proposes everything;
          review it, then Confirm. Signature fields stay blank for ink.
        </div>

        {!pdfFields ? <div style={{ fontSize: 12, color: '#6B7280' }}>Loading PDF fields…</div> : (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
              <button style={styles.btn} onClick={extract} disabled={!!busy}>
                {busy === 'extract' ? 'Reading the form with AI… (can take a minute)' : '✨ Auto-map with AI'}
              </button>
              <label style={{ fontSize: 12, color: '#374151' }}>
                Employees per copy:{' '}
                <input type="number" min="1" max="20" style={{ ...smallInput, width: 60, display: 'inline-block' }}
                  value={rows} onChange={e => setRows(Math.max(1, parseInt(e.target.value, 10) || 1))} />
              </label>
              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }} disabled={!!busy}
                title="Copy Row 1's source choices to the matching fields on every other row"
                onClick={copyRow1Pattern}>
                ⤓ Copy Row 1 to all rows
              </button>
              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }} disabled={!!busy}
                title="Download the PDF with every field filled by a label describing its mapping — open it to SEE which blank each row targets"
                onClick={() => {
                  const clean = fields.map(({ _pdf_type, ...f }) => f);
                  api.testFillBadgingMapping(file.id, clean, file.filename).catch(e => onError(e.message));
                }}>
                🧪 Test PDF
              </button>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{pdfFields.length} fields</span>
            </div>

            <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>PDF field</th>
                    <th style={thStyle}>Row</th>
                    <th style={thStyle}>Source</th>
                    <th style={thStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f, i) => (
                    <tr key={f.pdf_field} style={f._pdf_type === 'signature' ? { opacity: 0.5 } : undefined}>
                      <td style={{ ...tdStyle, maxWidth: 320 }} title={f.pdf_field}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                          {f.pdf_field}
                        </div>
                        <span style={{ fontSize: 9, color: '#9CA3AF' }}>{f._pdf_type}</span>
                      </td>
                      <td style={tdStyle}>
                        <input type="number" min="1" max={rows} style={{ ...smallInput, width: 48 }}
                          value={f.row || ''} placeholder="—"
                          disabled={f._pdf_type === 'signature'}
                          onChange={e => set(i, 'row', e.target.value ? parseInt(e.target.value, 10) : null)} />
                      </td>
                      <td style={tdStyle}>
                        <select style={{ ...smallInput, width: 150 }} value={f.source}
                          disabled={f._pdf_type === 'signature'}
                          onChange={e => set(i, 'source', e.target.value)}>
                          {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle}>
                        {f.source === 'employee' && (
                          <select style={{ ...smallInput, width: 260 }} value={f.source_key || ''}
                            onChange={e => set(i, 'source_key', e.target.value || null)}>
                            <option value="">— pick roster field —</option>
                            {Object.entries(profileKeys).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        )}
                        {(f.source === 'employee_ask' || f.source === 'shared_ask') && (
                          <input style={{ ...smallInput, width: 260 }} placeholder="Question to ask, e.g. Social Security Number"
                            value={f.question || ''} onChange={e => set(i, 'question', e.target.value)} />
                        )}
                        {f.source === 'constant' && (
                          <input style={{ ...smallInput, width: 260 }} placeholder="Fixed value"
                            value={f.constant_value || ''} onChange={e => set(i, 'constant_value', e.target.value)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={styles.btnSecondary} onClick={onClose} disabled={!!busy}>Cancel</button>
              <button style={styles.btnSecondary} onClick={() => save(false)} disabled={!!busy}>Save Draft</button>
              <button style={styles.btn} onClick={() => save(true)} disabled={!!busy}>
                {busy === 'save' ? 'Saving…' : '✓ Confirm Mapping'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee documents + badging info modal — used from Operations → Roster
// (📂 Docs) so dispatch can load DL / TWIC / cert scans and extract the
// badging facts ahead of time, outside any fill run.

export function EmployeeDocsModal({ employee, onClose, onError, onSaved }) {
  // Deactivated employees are sealed read-only server-side (workforce.js
  // assertEmployeeUnsealed on every mutating documents/badging-info/training
  // route) — mirrored here so a locked control doesn't just 403 silently.
  const sealed = !employee.is_active;
  const [docs, setDocs] = useState(null);
  const [info, setInfo] = useState({ ssn: '', drivers_license: '', dl_state: '', date_of_birth: '', twic_expiry: '' });
  // Home address lives on the EMPLOYEE record, not employee_badging_info — it
  // is roster data (it crosses to the Field Services crew roster so the PM can
  // bill mileage), so it is not behind the sensitive-info gate. It rides the
  // same AI document read because the driver's license shows it.
  const [addr, setAddr] = useState({
    address_line1: employee.address_line1 || '', address_line2: employee.address_line2 || '',
    city: employee.city || '', state: employee.state || '', zip: employee.zip || '',
  });
  const setA = (k, v) => setAddr(a => ({ ...a, [k]: k === 'state' ? v.toUpperCase() : v }));
  const [busy, setBusy] = useState(''); // '', 'upload', 'extract', 'save'
  const [savedFlash, setSavedFlash] = useState(false);
  // Server strips SSN / DL# / DOB for users with 'Hide sensitive
  // info' on (Admin → Users) and flags the response — mirror that here so
  // the locked fields don't render as innocently-empty inputs.
  const [sensitiveLocked, setSensitiveLocked] = useState(false);

  useEffect(() => {
    api.listEmployeeDocuments(employee.id).then(setDocs).catch(e => onError(e.message));
    api.getEmployeeBadgingInfo(employee.id)
      .then(i => {
        if (i && i.sensitive_locked) setSensitiveLocked(true);
        if (i) setInfo({
          ssn: i.ssn || '', drivers_license: i.drivers_license || '', dl_state: i.dl_state || '',
          date_of_birth: i.date_of_birth || '', twic_expiry: i.twic_expiry || '',
        });
      })
      .catch(e => onError(e.message));
  }, [employee.id]);

  const DOC_EXT = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];
  const [dragOver, setDragOver] = useState(false);
  // Selected canonical type labels the next upload — keeps employee doc
  // labels matching the customer's required-documents picker exactly.
  // '' = auto (label from filename; rename later if needed).
  const [docType, setDocType] = useState('');
  // Multi-file: most packets arrive as a handful of email attachments —
  // drag them all in one go. Uploaded sequentially (single-file endpoint).
  const upload = async (files) => {
    const all = Array.from(files || []).filter(Boolean);
    if (!all.length || busy) return;
    const ok = all.filter(f => DOC_EXT.includes(('.' + f.name.split('.').pop()).toLowerCase()));
    const skipped = all.filter(f => !ok.includes(f));
    if (!ok.length) { alert(`No uploadable files — allowed types: ${DOC_EXT.join(', ')}`); return; }
    if (docType && ok.length > 1) {
      alert(`A document type is selected ("${docType}") — drop ONE file at a time so each gets the right label, or switch the type back to Auto for a bulk drop.`);
      return;
    }
    setBusy('upload');
    try {
      for (const f of ok) {
        const doc = await api.uploadEmployeeDocument(employee.id, f, docType || undefined);
        // Server replaces same-named documents — drop superseded rows.
        setDocs(prev => [doc, ...(prev || []).filter(x => !(doc.replaced_ids || []).includes(x.id))]);
      }
      if (skipped.length) alert(`Uploaded ${ok.length}. Skipped (type not allowed): ${skipped.map(f => f.name).join(', ')}`);
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  const removeDoc = async (d) => {
    if (!confirm(`Remove "${d.filename}"?`)) return;
    try { await api.deleteEmployeeDocument(d.id); setDocs(prev => prev.filter(x => x.id !== d.id)); }
    catch (e) { onError(e.message); }
  };

  // Label drives the required-documents match — rename "MUELA CLASSES.pdf"
  // to "Safety Council Training" so the customer's checklist recognizes it.
  const renameDoc = async (d) => {
    const label = prompt(`Document label — use a standard type for automatic matching:\n${DOC_TYPES.join(' · ')}`, d.label || d.filename);
    if (label === null || !label.trim() || label.trim() === d.label) return;
    try {
      const updated = await api.renameEmployeeDocument(d.id, label.trim());
      setDocs(prev => prev.map(x => x.id === d.id ? { ...x, label: updated.label } : x));
    } catch (e) { onError(e.message); }
  };

  const extract = async () => {
    setBusy('extract');
    try {
      const r = await api.extractEmployeeBadgingInfo(employee.id);
      const next = {
        ssn: r.ssn || info.ssn, drivers_license: r.drivers_license || info.drivers_license,
        dl_state: r.dl_state || info.dl_state, date_of_birth: r.date_of_birth || info.date_of_birth,
        twic_expiry: r.twic_expiry || info.twic_expiry,
      };
      setInfo(next);
      // Address only fills BLANKS — a hand-corrected address always wins over
      // a fresh read of a possibly-stale license.
      const nextAddr = {
        address_line1: addr.address_line1 || r.address_line1 || '',
        address_line2: addr.address_line2 || r.address_line2 || '',
        city: addr.city || r.city || '',
        state: addr.state || r.state || '',
        zip: addr.zip || r.zip || '',
      };
      setAddr(nextAddr);
      await api.saveEmployeeBadgingInfo(employee.id, { ...next, extraction_model: r.extraction_model, extracted: true });
      await api.saveEmployeeAddress(employee.id, nextAddr);
      onSaved && onSaved({ ...employee, ...nextAddr });
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      await api.saveEmployeeBadgingInfo(employee.id, info);
      await api.saveEmployeeAddress(employee.id, addr);
      onSaved && onSaved({ ...employee, ...addr });
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  // Training extraction: AI reads the Safety Council history / certs in the
  // documents above, proposes the rows here for review, import writes them
  // to Operations → Training (+ DISA drug test date) on confirm.
  const [trainProposal, setTrainProposal] = useState(null); // { training:[{...,_checked}], drug_test_date }
  const [trainResult, setTrainResult] = useState('');
  const extractTraining = async () => {
    setBusy('train');
    setTrainResult('');
    try {
      const r = await api.extractEmployeeTraining(employee.id);
      setTrainProposal({
        drug_test_date: r.drug_test_date,
        training: (r.training || []).map(t => ({ ...t, _checked: true })),
      });
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };
  const importTraining = async () => {
    setBusy('train');
    try {
      const rows = trainProposal.training.filter(t => t._checked);
      const r = await api.importEmployeeTraining(employee.id, {
        training: rows,
        drug_test_date: trainProposal.drug_test_date,
      });
      setTrainProposal(null);
      setTrainResult(`✓ Saved ${r.inserted + r.updated} training record${r.inserted + r.updated === 1 ? '' : 's'}${r.drug_test_set ? ' + drug test date' : ''}.`);
    } catch (e) { onError(e.message); }
    finally { setBusy(''); }
  };

  const setI = (k, v) => setInfo(prev => ({ ...prev, [k]: v }));

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card(620)}>
        <h2 style={h2}>📂 Documents — {employee.full_name}</h2>
        <div style={sub}>
          DL / TWIC / training / test scans. Stored once here, attached to every badging submission, and readable by AI to fill the badging facts below.
        </div>
        {sealed && (
          <div style={{ background: '#FEF2F2', border: '1px solid #EF4444', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991B1B', marginBottom: 10 }}>
            🔒 {employee.full_name} is deactivated — documents and facts are sealed read-only. Downloads still work; reactivate on the Roster row to make changes.
          </div>
        )}

        {!docs ? <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div> : (
          <>
            {docs.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>Nothing on file yet.</div>}
            {docs.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 }}>
                <div style={{ flex: 1, minWidth: 0, cursor: sealed ? 'default' : 'pointer' }} title={sealed ? '' : 'Click to rename the label'} onClick={() => !sealed && renameDoc(d)}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {d.label || d.filename} {!sealed && <span style={{ fontSize: 10, color: '#9CA3AF' }}>✏</span>}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{d.filename}</div>
                </div>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
                  onClick={() => api.downloadEmployeeDocument(d.id, d.filename).catch(e => onError(e.message))}>⬇ Download</button>
                {!sealed && <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 10px' }} onClick={() => removeDoc(d)}>🗑 Delete</button>}
              </div>
            ))}
            {!sealed && <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>Label next upload as:</label>
              <select style={{ ...styles.input, fontSize: 12, padding: '4px 6px' }} value={docType} onChange={e => setDocType(e.target.value)}>
                <option value="">Auto (use filename)</option>
                {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <label
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
              style={{
                display: 'block', textAlign: 'center', cursor: 'pointer',
                padding: '14px 10px', borderRadius: 6, marginTop: 6,
                border: `2px dashed ${dragOver ? '#E36F2C' : '#D1D5DB'}`,
                background: dragOver ? '#FFF7ED' : '#FAFAFA',
                fontSize: 12, color: dragOver ? '#E36F2C' : '#6B7280',
              }}
            >
              {busy === 'upload'
                ? 'Uploading…'
                : docType
                  ? `⬆ Drop ONE file here to save it as "${docType}"`
                  : '⬆ Drag & drop the DL / TWIC / cert files here, or click to browse (multiple OK)'}
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif" style={{ display: 'none' }} disabled={!!busy}
                onChange={e => { upload(e.target.files); e.target.value = ''; }} />
            </label>
            </>}

            <div style={{ marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>Badging facts (pre-fill every badging form)</div>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
                  disabled={sealed || sensitiveLocked || !!busy || !docs.length}
                  title={sealed || sensitiveLocked ? '' : docs.length ? 'AI reads the documents above and fills these fields' : 'Upload documents first'}
                  onClick={extract}>
                  {busy === 'extract' ? 'Reading documents…' : '✨ Read documents with AI'}
                </button>
              </div>
              {sensitiveLocked && (
                <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#92400E', marginBottom: 8 }}>
                  🔒 SSN / DL# / DOB are hidden — your user has "Hide sensitive info" on (an admin can change it under Admin → Users).
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><label style={styles.label}>Social Security Number</label>
                  <input style={styles.input} value={sensitiveLocked ? '••••••' : info.ssn} disabled={sealed || sensitiveLocked} onChange={e => setI('ssn', e.target.value)} /></div>
                <div><label style={styles.label}>Driver's License #</label>
                  <input style={styles.input} value={sensitiveLocked ? '••••••' : info.drivers_license} disabled={sealed || sensitiveLocked} onChange={e => setI('drivers_license', e.target.value)} /></div>
                <div><label style={styles.label}>DL State</label>
                  <input style={styles.input} value={info.dl_state} disabled={sealed || sensitiveLocked} onChange={e => setI('dl_state', e.target.value)} /></div>
                <div><label style={styles.label}>Date of birth (MM/DD/YYYY)</label>
                  <input style={styles.input} value={sensitiveLocked ? '••••••' : info.date_of_birth} disabled={sealed || sensitiveLocked} onChange={e => setI('date_of_birth', e.target.value)} /></div>
                <div><label style={styles.label}>TWIC expires (MM/DD/YYYY)</label>
                  <input style={styles.input} value={info.twic_expiry} disabled={sealed || sensitiveLocked} onChange={e => setI('twic_expiry', e.target.value)} /></div>
              </div>
            </div>

            {/* Home address — NOT a sensitive badging fact: it goes to the
                Field Services crew roster so the PM can bill mileage. Read off
                the DL by the same "✨ Read documents with AI" button above. */}
            <div style={{ marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Home address</div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                Goes to the Field Services crew roster — the PM bills mileage off this. Read from the driver's license by the AI button above (it fills blanks only, so a corrected address is never overwritten).
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ gridColumn: '1 / -1' }}><label style={styles.label}>Street address</label>
                  <input style={styles.input} value={addr.address_line1} disabled={sealed} onChange={e => setA('address_line1', e.target.value)} /></div>
                <div><label style={styles.label}>Apt / Unit</label>
                  <input style={styles.input} value={addr.address_line2} disabled={sealed} onChange={e => setA('address_line2', e.target.value)} /></div>
                <div><label style={styles.label}>City</label>
                  <input style={styles.input} value={addr.city} disabled={sealed} onChange={e => setA('city', e.target.value)} /></div>
                <div><label style={styles.label}>State</label>
                  <input style={styles.input} maxLength={10} value={addr.state} disabled={sealed} onChange={e => setA('state', e.target.value)} /></div>
                <div><label style={styles.label}>Zip</label>
                  <input style={styles.input} maxLength={15} value={addr.zip} disabled={sealed} onChange={e => setA('zip', e.target.value)} /></div>
              </div>
            </div>

            <div style={{ marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>Training records (drives required-class checks)</div>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
                  disabled={sealed || !!busy || !docs.length}
                  title={sealed ? '' : docs.length ? 'AI reads the Safety Council history / certs above and proposes training records' : 'Upload documents first'}
                  onClick={extractTraining}>
                  {busy === 'train' && !trainProposal ? 'Reading documents…' : '🎓 Read training history with AI'}
                </button>
              </div>
              {trainResult && <div style={{ fontSize: 11, color: '#065F46', marginBottom: 6 }}>{trainResult}</div>}
              {trainProposal && (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 8 }}>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>
                    Review what was read — uncheck anything wrong, then save.
                    {trainProposal.drug_test_date && <> Drug test date found: <b>{trainProposal.drug_test_date}</b> (saves to DISA).</>}
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {trainProposal.training.map((t, i) => (
                      <label key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: 11, padding: '3px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={t._checked}
                          onChange={() => setTrainProposal(p => ({
                            ...p,
                            training: p.training.map((x, j) => j === i ? { ...x, _checked: !x._checked } : x),
                          }))} />
                        {t.code && <b style={{ whiteSpace: 'nowrap' }}>{t.code}</b>}
                        <span style={{ flex: 1 }}>{t.description}</span>
                        <span style={{ color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {t.issued_date || '—'} → {t.expiry_date || 'no expiry'}
                        </span>
                      </label>
                    ))}
                    {trainProposal.training.length === 0 && (
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>No training classes found in the documents.</div>
                    )}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }} onClick={() => setTrainProposal(null)} disabled={!!busy}>Cancel</button>
                    <button style={{ ...styles.btn, fontSize: 11, padding: '4px 10px' }} onClick={importTraining} disabled={!!busy || !trainProposal.training.some(t => t._checked)}>
                      {busy === 'train' ? 'Saving…' : `✓ Save ${trainProposal.training.filter(t => t._checked).length} record${trainProposal.training.filter(t => t._checked).length === 1 ? '' : 's'}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} onClick={onClose} disabled={!!busy}>Close</button>
              {!sealed && !sensitiveLocked && (
                <button style={{ ...styles.btn, ...(savedFlash ? { background: '#10B981' } : {}) }} onClick={save} disabled={!!busy}>
                  {busy === 'save' ? 'Saving…' : savedFlash ? '✓ Saved' : 'Save Facts'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fill wizard

export function FillWizard({ customerId, files, defaultEmail, requiredDocs, onClose, onError }) {
  // files = badging_files rows with mapping_status (only render entry when ≥1 Confirmed)
  // fillable_fields !== 0 excludes reference-only PDFs that got a Confirmed
  // mapping before the reference detection existed — nothing to populate.
  const confirmed = files.filter(f =>
    f.mapping_status === 'Confirmed' && /\.pdf$/i.test(f.filename) && f.fillable_fields !== 0);
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState(() => new Set(confirmed.map(f => f.id)));
  const [employees, setEmployees] = useState(null);   // roster
  const [chosenEmps, setChosenEmps] = useState([]);   // ordered ids
  const [empSearch, setEmpSearch] = useState('');
  const [ctx, setCtx] = useState(null);               // fill-context for picked forms
  const [shared, setShared] = useState({});
  const [perEmp, setPerEmp] = useState({});           // { empId: { ask_key: v } }
  const [emailTo, setEmailTo] = useState(defaultEmail || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Supporting documents (TWIC / DL / certs) per chosen employee.
  const [empDocs, setEmpDocs] = useState({});        // { empId: [docs] }
  const [docsChecked, setDocsChecked] = useState(new Set());
  const [docBusy, setDocBusy] = useState(0);         // employee id mid-upload

  useEffect(() => {
    api.listEmployees({ active: 'true' }).then(setEmployees).catch(e => onError(e.message));
  }, []);

  // Load each chosen employee's stored documents on the questions step (so
  // they can be uploaded/read there). Only documents matching the CUSTOMER'S
  // required-documents list come pre-checked — not every customer wants the
  // TWIC/DL attached. Anything else is one click away on the send step.
  const matchesRequired = (d) => (requiredDocs || []).some(req => {
    const needle = String(req).toLowerCase();
    return String(d.label || '').toLowerCase().includes(needle)
      || String(d.filename || '').toLowerCase().includes(needle);
  });
  useEffect(() => {
    if (step !== 3) return;
    Promise.all(chosenEmps.map(id => api.listEmployeeDocuments(id).then(docs => [id, docs])))
      .then(pairs => {
        setEmpDocs(Object.fromEntries(pairs));
        setDocsChecked(new Set(pairs.flatMap(([, docs]) => docs.filter(matchesRequired).map(d => d.id))));
      })
      .catch(e => onError(e.message));
  }, [step]);

  // Stored badging profile (SSN / DL / TWIC...) → answer keys. Ask keys are
  // canonical question slugs server-side, so each fact maps to the common
  // phrasings too. Extra keys a form doesn't use are ignored by the engine.
  const PROFILE_TO_ASK = {
    ssn: ['ssn', 'social_security_number'],
    drivers_license: ['drivers_license', 'driver_s_license', 'drivers_license_number'],
    dl_state: ['dl_state', 'driver_s_license_state', 'state'],
    date_of_birth: ['date_of_birth', 'dob'],
    twic_expiry: ['twic_expiry', 'date_twic_card_expires', 'twic_expiration_date'],
  };
  const mergeInfo = (empId, info, { overwrite = false } = {}) => {
    if (!info) return;
    setPerEmp(prev => {
      const cur = { ...(prev[empId] || {}) };
      for (const [k, askKeys] of Object.entries(PROFILE_TO_ASK)) {
        if (!info[k]) continue;
        for (const askKey of askKeys) {
          if (overwrite || !cur[askKey]) cur[askKey] = info[k];
        }
      }
      return { ...prev, [empId]: cur };
    });
  };

  // ✨ Read the employee's documents with AI, show the values in the inputs
  // (that IS the review), and save them to the profile for next time.
  const [extractBusy, setExtractBusy] = useState(0);
  const extractInfo = async (empId) => {
    setExtractBusy(empId);
    try {
      const info = await api.extractEmployeeBadgingInfo(empId);
      mergeInfo(empId, info, { overwrite: true });
      await api.saveEmployeeBadgingInfo(empId, { ...info, extracted: true });
    } catch (e) { onError(e.message); }
    finally { setExtractBusy(0); }
  };

  const toggleDoc = (id) => setDocsChecked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const uploadDoc = async (empId, file) => {
    if (!file) return;
    setDocBusy(empId);
    try {
      const doc = await api.uploadEmployeeDocument(empId, file);
      // Server replaces same-named documents — drop superseded rows and
      // uncheck them; the new copy is checked instead.
      const replaced = new Set(doc.replaced_ids || []);
      setEmpDocs(prev => ({ ...prev, [empId]: [doc, ...(prev[empId] || []).filter(d => !replaced.has(d.id))] }));
      setDocsChecked(prev => new Set([...[...prev].filter(id => !replaced.has(id)), doc.id]));
    } catch (e) { onError(e.message); }
    finally { setDocBusy(0); }
  };

  const togglePick = (id) => setPicked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleEmp = (id) => setChosenEmps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const goQuestions = async () => {
    try {
      const r = await api.badgingFillContext([...picked]);
      setCtx(r.forms);
      // Pre-fill per-employee answers from stored badging profiles.
      const infos = await Promise.all(chosenEmps.map(id =>
        api.getEmployeeBadgingInfo(id).then(info => [id, info]).catch(() => [id, null])));
      for (const [id, info] of infos) mergeInfo(id, info);
      setStep(3);
    } catch (e) { onError(e.message); }
  };

  // Deduped questions across the picked forms.
  const sharedAsks = useMemo(() => {
    const seen = new Map();
    for (const f of ctx || []) for (const a of f.shared_asks) if (!seen.has(a.ask_key)) seen.set(a.ask_key, a);
    return [...seen.values()];
  }, [ctx]);
  const empAsks = useMemo(() => {
    const seen = new Map();
    for (const f of ctx || []) for (const a of f.employee_asks) if (!seen.has(a.ask_key)) seen.set(a.ask_key, a);
    return [...seen.values()];
  }, [ctx]);

  const empById = useMemo(() => new Map((employees || []).map(e => [e.id, e])), [employees]);
  const filteredEmps = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return (employees || []).filter(e => !q || e.full_name.toLowerCase().includes(q));
  }, [employees, empSearch]);

  // First employee's answers mirror down: typing for employee 1 also fills
  // every other employee whose value is still empty or still matches what
  // employee 1 had before this keystroke. Hand-edited values never get
  // clobbered; editing employees 2+ only affects that employee.
  const setAnswer = (empId, askKey, value) => {
    setPerEmp(prev => {
      const firstId = chosenEmps[0];
      const next = { ...prev, [empId]: { ...(prev[empId] || {}), [askKey]: value } };
      if (empId === firstId) {
        const before = prev[firstId]?.[askKey] ?? '';
        for (const other of chosenEmps.slice(1)) {
          const cur = prev[other]?.[askKey] ?? '';
          if (cur === '' || cur === before) {
            next[other] = { ...(next[other] || prev[other] || {}), [askKey]: value };
          }
        }
      }
      return next;
    });
  };

  // Preflight before generating: the server resolves every field the same
  // way the real fill does and reports what would print blank + which
  // required customer documents an employee is missing. Generating with
  // blanks requires an explicit second click.
  const [preflight, setPreflight] = useState(null);
  const buildPayload = () => ({
    customer_id: customerId,
    file_ids: [...picked],
    employee_ids: chosenEmps,
    shared_answers: shared,
    employee_answers: perEmp,
    email_to: emailTo.trim() || undefined,
    attach_document_ids: [...docsChecked],
  });

  const fill = async (force = false) => {
    setBusy(true);
    try {
      if (!force) {
        // Always show the review panel — drug test age + cert status per
        // employee, plus anything that would print blank — before generating.
        const pf = await api.badgingFill({ ...buildPayload(), dry: true });
        setPreflight(pf);
        setBusy(false);
        return;
      }
      setPreflight(null);
      const r = await api.badgingFill(buildPayload());
      setResult(r);
      setStep(5);
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };
  const preflightHasIssues = !!(preflight && (
    preflight.blanks?.length || preflight.missing_docs?.length ||
    preflight.compliance?.some(c => c.issues?.length)));

  const stepTitle = {
    1: 'Step 1 of 4 — Pick the forms',
    2: 'Step 2 of 4 — Pick the employees',
    3: 'Step 3 of 4 — Fill in the blanks (each asked once)',
    4: 'Step 4 of 4 — Generate & send',
    5: 'Done',
  }[step];

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div style={card(760)}>
        <h2 style={h2}>🖨 Fill Badging Forms</h2>
        <div style={sub}>{stepTitle}</div>

        {step === 1 && (
          <>
            {confirmed.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF' }}>No forms have a Confirmed mapping yet.</div>}
            {confirmed.map(f => (
              <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 4px', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.has(f.id)} onChange={() => togglePick(f.id)} />
                📎 {f.filename}
              </label>
            ))}
            {(() => {
              // Only nag about forms that COULD be filled but lack a confirmed
              // mapping — reference-only documents don't belong in this hint.
              const unmapped = files.filter(f =>
                f.mapping_status !== 'Confirmed' && /\.pdf$/i.test(f.filename) && f.fillable_fields !== 0);
              return unmapped.length > 0 && (
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
                  Mappable but not confirmed yet: {unmapped.map(f => f.filename).join(' · ')}
                </div>
              );
            })()}
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} onClick={onClose}>Cancel</button>
              <button style={styles.btn} disabled={!picked.size} onClick={() => setStep(2)}>Next →</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <input style={{ ...styles.input, marginBottom: 8 }} placeholder="Search the roster…"
              value={empSearch} onChange={e => setEmpSearch(e.target.value)} />
            <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6, padding: 6 }}>
              {!employees && <div style={{ fontSize: 12, color: '#6B7280' }}>Loading roster…</div>}
              {filteredEmps.map(e => (
                <label key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 4px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={chosenEmps.includes(e.id)} onChange={() => toggleEmp(e.id)} />
                  {e.full_name}
                  <span style={{ fontSize: 10, color: '#9CA3AF' }}>{e.craft || ''}</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
              {chosenEmps.length} selected. Forms with multi-employee rows get extra copies automatically when you pick more people than a form holds.
            </div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
              <button style={styles.btnSecondary} onClick={() => setStep(1)}>← Back</button>
              <button style={styles.btn} disabled={!chosenEmps.length} onClick={goQuestions}>Next →</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            {sharedAsks.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>For the whole packet</div>
                {sharedAsks.map(a => (
                  <div key={a.ask_key} style={{ marginBottom: 6 }}>
                    <label style={styles.label}>{a.question}</label>
                    <input style={styles.input} value={shared[a.ask_key] || ''}
                      onChange={e => setShared({ ...shared, [a.ask_key]: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
            {empAsks.length > 0 && chosenEmps.length > 1 && (
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                ℹ The first employee's answers copy down to everyone automatically — edit any employee below to override theirs.
              </div>
            )}
            {empAsks.length > 0 && chosenEmps.map(id => (
              <div key={id} style={{ marginBottom: 12, border: '1px solid #E5E7EB', borderRadius: 6, padding: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{empById.get(id)?.full_name}</div>
                  <DropZone onFiles={files => uploadDoc(id, files[0])} disabled={!!docBusy} title="Drop the document here">
                    <label style={{ ...styles.btnSecondary, fontSize: 10, padding: '3px 8px', cursor: 'pointer', display: 'inline-block' }}
                      title="Upload this employee's DL / TWIC / cert scans (stored on their roster record)">
                      {docBusy === id ? '…' : `⬆ Add document${(empDocs[id]?.length || 0) ? ` (${empDocs[id].length})` : ''}`}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" style={{ display: 'none' }} disabled={!!docBusy}
                        onChange={e => { uploadDoc(id, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  </DropZone>
                  <button style={{ ...styles.btnSecondary, fontSize: 10, padding: '3px 8px' }}
                    disabled={!!extractBusy || !(empDocs[id]?.length)}
                    title={(empDocs[id]?.length) ? 'AI reads the uploaded documents and fills these answers (saved for next time)' : 'Upload documents first'}
                    onClick={() => extractInfo(id)}>
                    {extractBusy === id ? 'Reading…' : '✨ From documents'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                  {empAsks.map(a => (
                    <div key={a.ask_key}>
                      <label style={styles.label}>{a.question}</label>
                      <input style={styles.input} value={perEmp[id]?.[a.ask_key] || ''}
                        onChange={e => setAnswer(id, a.ask_key, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {sharedAsks.length === 0 && empAsks.length === 0 && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>Nothing to ask — everything fills from the roster and constants.</div>
            )}
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
              Typed answers go straight into the PDFs. ✨ From documents also saves the values to the employee's profile so they pre-fill next time.
            </div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
              <button style={styles.btnSecondary} onClick={() => setStep(2)}>← Back</button>
              <button style={styles.btn} onClick={() => setStep(4)}>Next →</button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div style={{ fontSize: 12, marginBottom: 10 }}>
              <b>{picked.size}</b> form{picked.size === 1 ? '' : 's'} × <b>{chosenEmps.length}</b> employee{chosenEmps.length === 1 ? '' : 's'} —
              filled copies are saved on this customer and can be downloaded after.
            </div>

            {/* Supporting documents: TWIC / DL / certs stored on the roster
                employee, attached to the submission email. */}
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Supporting documents to attach</div>
            <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 6 }}>
              {(requiredDocs?.length)
                ? 'Pre-checked to match this customer\'s required-documents list — tick anything extra to include it.'
                : 'This customer has no required-documents list, so nothing is pre-checked — tick what should ride along.'}
            </div>
            {chosenEmps.map(id => (
              <div key={id} style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{empById.get(id)?.full_name}</div>
                  <DropZone onFiles={files => uploadDoc(id, files[0])} disabled={!!docBusy} title="Drop the document here">
                    <label style={{ ...styles.btnSecondary, fontSize: 10, padding: '3px 8px', cursor: 'pointer', display: 'inline-block' }}>
                      {docBusy === id ? '…' : '⬆ Add document'}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" style={{ display: 'none' }} disabled={!!docBusy}
                        onChange={e => { uploadDoc(id, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  </DropZone>
                </div>
                {(empDocs[id] || []).length === 0 && (
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
                    Nothing on file yet — add the TWIC, DL, training, and test documents once; they're saved for every future submission.
                  </div>
                )}
                {(empDocs[id] || []).map(d => (
                  <label key={d.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, marginTop: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={docsChecked.has(d.id)} onChange={() => toggleDoc(d.id)} />
                    📎 {d.label || d.filename}
                    <span style={{ fontSize: 9, color: '#9CA3AF' }}>{d.filename}</span>
                  </label>
                ))}
              </div>
            ))}

            <label style={styles.label}>Email the packet to (optional)</label>
            <input style={styles.input} placeholder="dispatch@selectsafetyusa.com, pm@customer.com"
              value={emailTo} onChange={e => setEmailTo(e.target.value)} />
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
              Sends from noreply@selectsafetyusa.com with the filled PDFs attached. Leave blank to just generate.
            </div>
            {preflight && (
              <div style={{
                marginTop: 12, borderRadius: 6, padding: 10,
                background: preflightHasIssues ? '#FFFBEB' : '#ECFDF5',
                border: `1px solid ${preflightHasIssues ? '#F59E0B' : '#6EE7B7'}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: preflightHasIssues ? '#92400E' : '#065F46', marginBottom: 6 }}>
                  {preflightHasIssues ? '⚠ Review before generating' : '✓ Review — everything checks out'}
                </div>
                {preflight.compliance?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {preflight.compliance.map((c, i) => (
                      <div key={i} style={{ fontSize: 11, marginBottom: 4 }}>
                        <b>{c.employee}</b>{' — '}
                        {c.drug_test_age_days === null
                          ? <span style={{ color: '#B45309' }}>no drug test on record</span>
                          : <span style={{ color: (c.drug_test_max_days && c.drug_test_age_days > c.drug_test_max_days) ? '#B45309' : '#065F46' }}>
                              drug test {c.drug_test_age_days} day{c.drug_test_age_days === 1 ? '' : 's'} old
                              {c.drug_test_max_days ? ` (max ${c.drug_test_max_days})` : ''}
                            </span>}
                        {c.issues.filter(x => !x.startsWith('No drug test')).map((x, j) => (
                          <div key={j} style={{ color: '#B45309', marginLeft: 12 }}>⚠ {x}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {preflight.blanks?.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>
                      These will print BLANK ({preflight.blanks.length}):
                    </div>
                    <ul style={{ margin: '4px 0 8px', paddingLeft: 18, fontSize: 11, color: '#78350F', maxHeight: 140, overflowY: 'auto' }}>
                      {preflight.blanks.map((bk, i) => (
                        <li key={i}>{bk.label}{bk.employee ? ` — ${bk.employee}` : ''} <span style={{ color: '#B45309' }}>({bk.form.slice(0, 35)}…)</span></li>
                      ))}
                    </ul>
                  </>
                )}
                {preflight.missing_docs?.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>
                      Required documents NOT on file:
                    </div>
                    <ul style={{ margin: '4px 0 8px', paddingLeft: 18, fontSize: 11, color: '#78350F' }}>
                      {preflight.missing_docs.map((m, i) => (
                        <li key={i}>{m.employee}: {m.required}</li>
                      ))}
                    </ul>
                  </>
                )}
                {preflightHasIssues && (
                  <div style={{ fontSize: 10, color: '#92400E' }}>
                    Go back to fix answers / add documents, or generate anyway if the gaps are intentional.
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
              <button style={styles.btnSecondary} onClick={() => { setPreflight(null); setStep(3); }} disabled={busy}>← Back</button>
              {preflight ? (
                <button
                  style={{ ...styles.btn, ...(preflightHasIssues ? { background: '#B45309' } : {}) }}
                  onClick={() => fill(true)} disabled={busy}>
                  {busy ? 'Filling forms…' : preflightHasIssues ? '⚠ Generate Anyway' : '✓ Generate Packet'}
                </button>
              ) : (
                <button style={styles.btn} onClick={() => fill(false)} disabled={busy}>
                  {busy ? 'Checking…' : '🖨 Review & Generate'}
                </button>
              )}
            </div>
          </>
        )}

        {step === 5 && result && (
          <>
            <div style={{ background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 10 }}>
              ✓ Generated {result.files.length} filled PDF{result.files.length === 1 ? '' : 's'}.
              {result.email?.sent && <> Emailed to <b>{result.run.emailed_to}</b>.</>}
              {result.email && !result.email.sent && <span style={{ color: '#B45309' }}> Email failed: {result.email.error}</span>}
            </div>
            {result.files.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.filename}</span>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                  onClick={() => api.downloadBadgingRunFile(f.id, f.filename).catch(e => onError(e.message))}>⬇ Download</button>
              </div>
            ))}
            {result.warnings?.length > 0 && (
              <div style={{ fontSize: 10, color: '#B45309', marginTop: 8 }}>
                {result.warnings.length} field warning{result.warnings.length === 1 ? '' : 's'} (skipped): {result.warnings.slice(0, 5).join(' · ')}
              </div>
            )}
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
              <button style={styles.btn} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
